import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";

import Menu from "../components/Menu";
import api from "../services/api";
import logo from "../assets/images/logo.png";


const parseMetragem = (val) =>
    parseFloat(String(val ?? "0").replace(",", ".")) || 0;

const fmtNum = (val) =>
    (val || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    });

const fmtDate = (date) => {
    if (!date) return "";
    return `${String(date.getDate()).padStart(2, "0")}/${String(
        date.getMonth() + 1
    ).padStart(2, "0")}/${date.getFullYear()}`;
};

const apiDate = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;


function buildRelatorio(historico, filtroOperador, filtroTurma, filtro100m) {
    if (!Array.isArray(historico) || historico.length === 0) return null;

    // 1. Aplicar filtros de turma e operador
    let dados = historico.filter((item) => {
        if (filtroTurma && String(item.turma) !== String(filtroTurma)) return false;
        if (filtroOperador && item.colaborador !== filtroOperador) return false;
        return true;
    });

    if (dados.length === 0) return null;

    // 2. Filtro 100m/dia: incluir apenas registros de datas onde o operador
    //    teve TOTAL >= 100m naquele dia
    if (filtro100m) {
        // Agrupa metragem por colaborador+data
        const metPorDia = {};
        dados.forEach((item) => {
            const key = `${item.colaborador}||${item.data || item.dataf || ""}`;
            metPorDia[key] = (metPorDia[key] || 0) + parseMetragem(item.metragem);
        });
        dados = dados.filter((item) => {
            const key = `${item.colaborador}||${item.data || item.dataf || ""}`;
            return (metPorDia[key] || 0) >= 100;
        });
    }

    if (dados.length === 0) return null;

    // 3. Agrupa por operador
    const operadoresMap = {};
    dados.forEach((item) => {
        const op = item.colaborador || "SEM NOME";
        if (!operadoresMap[op]) operadoresMap[op] = [];
        operadoresMap[op].push(item);
    });

    // 4. Para cada operador: top 4 teares, top 3 defeitos por tear
    const resultado = Object.entries(operadoresMap).map(([operador, items]) => {
        // Agrupa por tear
        const tearMap = {};
        items.forEach((item) => {
            const tear = String(item.tear || "?");
            if (!tearMap[tear]) tearMap[tear] = {};
            const def = item.defeito || "SEM DEFEITO";
            tearMap[tear][def] = (tearMap[tear][def] || 0) + parseMetragem(item.metragem);
        });

        // Ordena teares pelo total decrescente, pega top 4
        const tearesOrdenados = Object.entries(tearMap)
            .map(([tear, defMap]) => {
                const totalTear = Object.values(defMap).reduce((a, b) => a + b, 0);
                return { tear, defMap, totalTear };
            })
            .sort((a, b) => b.totalTear - a.totalTear)
            .slice(0, 4);

        // Para cada tear: top 3 defeitos decrescente
        const teares = tearesOrdenados.map(({ tear, defMap, totalTear }) => {
            const defeitosOrdenados = Object.entries(defMap)
                .sort((a, b) => b[1] - a[1]);

            const listaDefeitos = defeitosOrdenados
                .slice(0, 3)
                .map(([nome, metragem]) => ({ nome, metragem, isOutros: false }));

            const resto = defeitosOrdenados.slice(3);

            if (resto.length > 0) {
                const somaOutros = resto.reduce((acc, curr) => acc + curr[1], 0);
                const detalhesOutros = resto.map(([nome, met]) => `${nome}: ${fmtNum(met)}m`).join(" | ");
                
                listaDefeitos.push({
                    nome: "Outros",
                    metragem: somaOutros,
                    isOutros: true,
                    detalhes: detalhesOutros
                });
            }

            return { tear, defeitos: listaDefeitos, totalTear };
        });

        const totalGeral = teares.reduce((acc, t) => acc + t.totalTear, 0);

        // Turma do operador: pega a turma mais frequente nos registros dele
        const turmaContagem = {};
        items.forEach((item) => {
            const t = String(item.turma || "");
            if (t) turmaContagem[t] = (turmaContagem[t] || 0) + 1;
        });
        const turmaOperador = Object.entries(turmaContagem)
            .sort((a, b) => b[1] - a[1])
            .map(([t]) => t)[0] || "";

        return { operador, turma: turmaOperador, teares, totalGeral };
    });

    return resultado;
}

function RelatorioImpressao({ ref: _ref, innerRef, resultado, initialDate, finalDate, filtroTurma }) {
    return (
        <div ref={innerRef} style={{ fontFamily: "Arial, sans-serif", background: "#fff" }}>
            {resultado.map((item, idx) => (
                <div
                    key={idx}
                    style={{
                        pageBreakAfter: idx < resultado.length - 1 ? "always" : "auto",
                        padding: "24px 28px",
                        minHeight: "100vh",
                        boxSizing: "border-box",
                    }}
                >
                    {/* ── Cabeçalho ── */}
                    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14 }}>
                        <tbody>
                            <tr>
                                <td style={{ width: "30%", border: "2px solid #333", padding: "6px 10px" }}>
                                    <img src={logo} alt="Logo Cambos" style={{ height: 56, display: "block" }} />
                                </td>
                                <td
                                    style={{
                                        border: "2px solid #333",
                                        padding: "6px 10px",
                                        textAlign: "center",
                                        fontWeight: "bold",
                                        fontSize: 20,
                                        letterSpacing: 1,
                                    }}
                                >
                                    RELATÓRIO DE DEFEITO POR TECELÃO
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* ── Filtros aplicados ── */}
                    <table
                        style={{
                            borderCollapse: "collapse",
                            marginBottom: 18,
                            fontSize: 16, // Aumentado de 13 para 16
                        }}
                    >
                        <tbody>
                            <InfoRow label="Data Início" value={fmtDate(initialDate)} />
                            <InfoRow label="Data Fim" value={fmtDate(finalDate)} />
                            <InfoRow label="Turma" value={item.turma || "N/D"} />
                        </tbody>
                    </table>

                    {/* ── Nome do Tecelão ── */}
                    <div
                        style={{
                            border: "1px solid #333",
                            padding: "10px 14px",
                            fontWeight: "bold",
                            fontSize: 20, // Aumentado de 15 para 20
                            marginBottom: 20,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <span>TECELÃO: {item.operador.toUpperCase()}</span>
                        <span
                            style={{
                                border: "2px solid #e8a000",
                                padding: "6px 14px",
                                borderRadius: 4,
                                fontWeight: "bold",
                                fontSize: 18, // Aumentado de 15 para 18
                            }}
                        >
                            Total geral: {fmtNum(item.totalGeral)}
                        </span>
                    </div>

                    {/* ── Header de colunas ── */}
                    <div style={{ display: "flex", gap: 32, marginBottom: 10, paddingLeft: 4 }}>
                        <div
                            style={{
                                width: 110, // Ajustado para acomodar a nova fonte
                                border: "1px solid #4caf50",
                                borderRadius: 4,
                                textAlign: "center",
                                padding: "6px 0",
                                fontWeight: "bold",
                                fontSize: 16, // Aumentado de 13 para 16
                            }}
                        >
                            Tear
                        </div>
                        <div style={{ width: 14 }} /> {/* Ajuste de espaçamento para manter alinhamento */}
                        <div
                            style={{
                                border: "1px solid #e8a000",
                                borderRadius: 4,
                                padding: "6px 18px",
                                fontWeight: "bold",
                                fontSize: 16, // Aumentado de 13 para 16
                            }}
                        >
                            Defeitos/Metragem
                        </div>
                    </div>

                    {/* ── Teares ── */}
                    {item.teares.map((tearItem, ti) => (
                        <div
                            key={ti}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 16,
                                marginBottom: 22,
                            }}
                        >
                            {/* Ícone do tear */}
                            <div
                                style={{
                                    width: 110, // Aumentado de 96 para 110
                                    height: 100, // Aumentado de 90 para 100
                                    background: "#3a7cbf",
                                    borderRadius: 10,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    fontWeight: "bold",
                                    fontSize: 32, // Aumentado de 28 para 32
                                    flexShrink: 0,
                                }}
                            >
                                {tearItem.tear}
                            </div>

                            {/* Seta */}
                            <div style={{ fontSize: 32, color: "#333", flexShrink: 0 }}>→</div>

                            {/* Caixa de defeitos */}
                            <div
                                style={{
                                    border: "1px solid #bbb",
                                    borderRadius: 8,
                                    padding: "12px 18px",
                                    minWidth: 250,
                                    fontSize: 16,
                                    lineHeight: "1.8",
                                }}
                            >
                                {tearItem.defeitos.map((d, di) => (
                                    <div key={di} style={{ marginBottom: d.isOutros ? 8 : 0 }}>
                                        <div>{d.nome}: {fmtNum(d.metragem)}</div>
                                        {d.isOutros && (
                                            <div style={{ fontSize: 12, color: "#555", fontStyle: "italic", lineHeight: "1.4", paddingLeft: 8 }}>
                                                ({d.detalhes})
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <div style={{ fontWeight: "bold", marginTop: 4, borderTop: "1px solid #ddd", paddingTop: 4 }}>
                                    Total: {fmtNum(tearItem.totalTear)}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* ── Assinaturas ── */}
                    <div style={{ marginTop: 40 }}>
                        <div
                            style={{
                                border: "1px solid #333",
                                padding: "12px 12px",
                                marginBottom: 12,
                                fontSize: 16,
                            }}
                        >
                            ASSINATURA TECELÃO:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </div>
                        <div
                            style={{
                                border: "1px solid #333",
                                padding: "12px 12px",
                                fontSize: 16,
                            }}
                        >
                            ASSINATURA LÍDER:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}


function PreviewOperador({ resultado, initialDate, finalDate, filtroTurma }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            {resultado.map((item, idx) => (
                <div
                    key={idx}
                    style={{
                        background: "#fff",
                        borderRadius: 12,
                        border: "1px solid #dee2e6",
                        overflow: "hidden",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}
                >
                    {/* Cabeçalho do operador */}
                    <div
                        style={{
                            background: "linear-gradient(135deg, #115a77 0%, #1a7fa8 100%)",
                            padding: "16px 24px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 12,
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div
                                style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: "50%",
                                    background: "rgba(255,255,255,0.2)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <i className="pi pi-user" style={{ color: "#fff", fontSize: 20 }} />
                            </div>
                            <div>
                                <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                                    Tecelão
                                </div>
                                <div style={{ color: "#fff", fontSize: 17, fontWeight: "bold" }}>
                                    {item.operador.toUpperCase()}
                                </div>
                            </div>
                        </div>
                        <div
                            style={{
                                background: "rgba(255,255,255,0.15)",
                                border: "1.5px solid rgba(255,200,0,0.6)",
                                borderRadius: 8,
                                padding: "8px 18px",
                                textAlign: "center",
                            }}
                        >
                            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>Total Geral</div>
                            <div style={{ color: "#ffd740", fontSize: 22, fontWeight: "bold" }}>
                                {fmtNum(item.totalGeral)} <span style={{ fontSize: 13 }}>m</span>
                            </div>
                        </div>
                    </div>

                    {/* Info do período/turma */}
                    <div
                        style={{
                            background: "#f0f7fb",
                            padding: "8px 24px",
                            display: "flex",
                            gap: 24,
                            fontSize: 12,
                            color: "#5a8fa8",
                            flexWrap: "wrap",
                            borderBottom: "1px solid #dce9f0",
                        }}
                    >
                        <span><strong>Período:</strong> {fmtDate(initialDate)} — {fmtDate(finalDate)}</span>
                        <span><strong>Turma:</strong> {item.turma || "N/D"}</span>
                    </div>

                    {/* Grid de teares */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                            gap: 16,
                            padding: 20,
                        }}
                    >
                        {item.teares.map((tearItem, ti) => (
                            <div
                                key={ti}
                                style={{
                                    background: "#f8fbff",
                                    border: "1px solid #c8dff0",
                                    borderRadius: 10,
                                    overflow: "hidden",
                                }}
                            >
                                {/* Número do tear */}
                                <div
                                    style={{
                                        background: "linear-gradient(135deg, #3a7cbf, #5a9fd4)",
                                        padding: "12px 16px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 8,
                                            background: "rgba(255,255,255,0.2)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#fff",
                                            fontWeight: "bold",
                                            fontSize: 20,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {tearItem.tear}
                                    </div>
                                    <div>
                                        <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 10, fontWeight: 600, textTransform: "uppercase" }}>Tear</div>
                                        <div style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>
                                            Total: {fmtNum(tearItem.totalTear)} m
                                        </div>
                                    </div>
                                </div>

                                {/* Defeitos */}
                               <div style={{ padding: "10px 14px" }}>
                                    {tearItem.defeitos.map((d, di) => (
                                        <div
                                            key={di}
                                            title={d.isOutros ? `Composto por: ${d.detalhes}` : ""}
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                padding: "5px 0",
                                                borderBottom: di < tearItem.defeitos.length - 1 ? "1px solid #e8f0f7" : "none",
                                                cursor: d.isOutros ? "help" : "default"
                                            }}
                                        >
                                            <span style={{ fontSize: 12, color: "#495057", flex: 1, marginRight: 8 }}>
                                                <span
                                                    style={{
                                                        display: "inline-block",
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: "50%",
                                                        background: di === 0 ? "#e63946" : di === 1 ? "#eab308" : di === 2 ? "#457b9d" : "#6c757d", 
                                                        color: "#fff",
                                                        fontSize: 10,
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                        lineHeight: "18px",
                                                        marginRight: 6,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {di + 1}
                                                </span>
                                                {d.nome}
                                            </span>
                                            <span
                                                style={{
                                                    fontWeight: "bold",
                                                    color: "#115a77",
                                                    fontSize: 13,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {fmtNum(d.metragem)} m
                                            </span>
                                        </div>
                                    ))}

                                    {/* Total do tear */}
                                    <div
                                        style={{
                                            marginTop: 8,
                                            paddingTop: 6,
                                            borderTop: "2px solid #c8dff0",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            fontWeight: "bold",
                                            fontSize: 13,
                                        }}
                                    >
                                        <span style={{ color: "#115a77" }}>Total</span>
                                        <span style={{ color: "#115a77" }}>{fmtNum(tearItem.totalTear)} m</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

// Linha de info nos filtros
function InfoRow({ label, value }) {
    return (
        <tr>
            <td
                style={{
                    border: "1px solid #bbb",
                    padding: "6px 12px",
                    backgroundColor: "#e0f0f8",
                    color: "#1a5f8a",
                    fontWeight: "bold",
                    width: 140,
                    fontSize: 16,
                }}
            >
                {label}
            </td>
            <td
                style={{
                    border: "1px solid #bbb",
                    padding: "6px 14px",
                    fontSize: 16,
                    minWidth: 160,
                }}
            >
                {value}
            </td>
        </tr>
    );
}


export default function RelatorioQualidadeOperador() {
    const navigate = useNavigate();
    const toast = useRef(null);
    const printRef = useRef(null);

    const hoje = new Date();
    const [initialDate, setInitialDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return d;
    });
    const [finalDate, setFinalDate] = useState(hoje);

    const [historicoRaw, setHistoricoRaw] = useState([]);
    const [loading, setLoading] = useState(false);

    const [filtroTurma, setFiltroTurma] = useState(null);
    const [filtroOperador, setFiltroOperador] = useState(null);
    const [filtro100m, setFiltro100m] = useState(false);

    // Listas derivadas para os dropdowns
    const turmasDisponiveis = useMemo(() => {
        const set = new Set(historicoRaw.map((i) => String(i.turma)).filter(Boolean));
        return Array.from(set)
            .sort()
            .map((t) => ({ label: `Turma ${t}`, value: t }));
    }, [historicoRaw]);

    const operadoresDisponiveis = useMemo(() => {
        const porTurma = filtroTurma
            ? historicoRaw.filter((i) => String(i.turma) === String(filtroTurma))
            : historicoRaw;
        const set = new Set(porTurma.map((i) => i.colaborador).filter(Boolean));
        return Array.from(set)
            .sort()
            .map((op) => ({ label: op, value: op }));
    }, [historicoRaw, filtroTurma]);

    // Limpa operador quando muda a turma
    useEffect(() => {
        setFiltroOperador(null);
    }, [filtroTurma]);

    // Buscar dados 
    const buscarDados = () => {
        if (!initialDate || !finalDate) {
            toast.current?.show({
                summary: "Aviso",
                severity: "warn",
                detail: "Preencha as datas de início e fim.",
            });
            return;
        }

        setLoading(true);
        const d1 = apiDate(initialDate);
        const d2 = apiDate(finalDate);

        api
            .get(`defeitos/historico/${d1}/${d2}`)
            .then((response) => {
                const hist = response.data?.historico || [];
                setHistoricoRaw(Array.isArray(hist) ? hist : []);
                if (hist.length === 0) {
                    toast.current?.show({
                        summary: "Sem dados",
                        severity: "info",
                        detail: "Nenhum registro encontrado para o período informado.",
                    });
                } else {
                    toast.current?.show({
                        summary: "Concluído",
                        severity: "success",
                        detail: `${hist.length} registros carregados.`,
                    });
                }
            })
            .catch((error) => {
                toast.current?.show({
                    summary: "Erro",
                    severity: "error",
                    detail: error.response ? error.response.data.message : error.message,
                });
            })
            .finally(() => setLoading(false));
    };

    //  Resultado do relatório 
    const resultado = useMemo(
        () => buildRelatorio(historicoRaw, filtroOperador, filtroTurma, filtro100m),
        [historicoRaw, filtroOperador, filtroTurma, filtro100m]
    );

    //  Imprimir 
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        pageStyle: `
            @page { size: A4 portrait; margin: 10mm; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        `,
    });

    return (
        <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh", paddingBottom: "3rem" }}>
            <Menu />
            <Toast ref={toast} />

            <div className="p-m-3 p-mt-4">
                <Card
                    className="p-shadow-2"
                    style={{ borderRadius: 8 }}
                    header={
                        <div
                            className="p-d-flex p-jc-between p-ai-center p-p-3"
                            style={{ borderBottom: "1px solid #dee2e6" }}
                        >
                            <div>
                                <h1 className="p-m-0" style={{ fontSize: "1.6rem", fontWeight: "bold" }}>
                                    Relatório de Qualidade por Operador
                                </h1>
                                <small style={{ color: "#6c757d", lineHeight: "1.5", display: "block", marginTop: 4 }}>
                                    Exibe os <strong>4 teares com maior volume de defeitos</strong> do operador no período selecionado.
                                    Para cada tear, são listados os <strong>3 defeitos mais frequentes</strong> em ordem decrescente de metragem,
                                    com o total individual por tear e o <strong>total geral de defeitos</strong>.
                                </small>
                            </div>
                            <Button
                                className="p-button-text p-button-secondary"
                                icon="pi pi-arrow-left"
                                label="Voltar"
                                onClick={() => navigate("/defeitos/relatorio-diario-qualidade")}
                            />
                        </div>
                    }
                >
                    <div
                        className="p-fluid p-formgrid p-grid p-ai-end"
                        style={{
                            backgroundColor: "#fff",
                            padding: "1rem",
                            borderRadius: 6,
                            border: "1px solid #e9ecef",
                            marginBottom: "1.5rem",
                            gap: "0.5rem",
                        }}
                    >
                        {/* Data Início */}
                        <div className="p-field p-col-12 p-md-2">
                            <label style={{ fontWeight: "600", color: "#495057" }}>Data Início</label>
                            <Calendar
                                dateFormat="dd/mm/yy"
                                mask="99/99/9999"
                                value={initialDate}
                                onChange={(e) => setInitialDate(e.value)}
                                showIcon
                            />
                        </div>

                        {/* Data Fim */}
                        <div className="p-field p-col-12 p-md-2">
                            <label style={{ fontWeight: "600", color: "#495057" }}>Data Fim</label>
                            <Calendar
                                dateFormat="dd/mm/yy"
                                mask="99/99/9999"
                                value={finalDate}
                                onChange={(e) => setFinalDate(e.value)}
                                showIcon
                            />
                        </div>

                        {/* Buscar */}
                        <div className="p-field p-col-12 p-md-1">
                            <label>&nbsp;</label>
                            <Button
                                icon="pi pi-search"
                                className="p-button-primary"
                                style={{ backgroundColor: "#115a77", borderColor: "#115a77" }}
                                onClick={buscarDados}
                                loading={loading}
                                tooltip="Buscar período"
                            />
                        </div>

                        {/* Turma */}
                        <div className="p-field p-col-12 p-md-2">
                            <label style={{ fontWeight: "600", color: "#495057" }}>Turma</label>
                            <Dropdown
                                value={filtroTurma}
                                options={turmasDisponiveis}
                                onChange={(e) => setFiltroTurma(e.value)}
                                placeholder="Todas"
                                showClear
                                disabled={turmasDisponiveis.length === 0}
                            />
                        </div>

                        {/* Operador */}
                        <div className="p-field p-col-12 p-md-3">
                            <label style={{ fontWeight: "600", color: "#495057" }}>Operador</label>
                            <Dropdown
                                value={filtroOperador}
                                options={operadoresDisponiveis}
                                onChange={(e) => setFiltroOperador(e.value)}
                                placeholder="Todos"
                                filter
                                showClear
                                disabled={operadoresDisponiveis.length === 0}
                                emptyFilterMessage="Nenhum operador encontrado"
                            />
                        </div>

                        {/* Filtro 100m/dia */}
                        <div className="p-field p-col-12 p-md-2" style={{ display: "flex", flexDirection: "column" }}>
                            <label style={{ fontWeight: "600", color: "#495057", marginBottom: 6 }}>
                                Apenas ≥ 100m/dia
                            </label>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                <InputSwitch
                                    checked={filtro100m}
                                    onChange={(e) => setFiltro100m(e.value)}
                                />
                                <span style={{ fontSize: 13, color: filtro100m ? "#115a77" : "#aaa" }}>
                                    {filtro100m ? "Ativo" : "Inativo"}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/*  Loading  */}
                    {loading && (
                        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                            <ProgressSpinner style={{ width: 40, height: 40 }} strokeWidth="4" />
                        </div>
                    )}

                    {/*  Resultado  */}
                    {!loading && historicoRaw.length > 0 && (
                        <>
                            {resultado === null || resultado.length === 0 ? (
                                <div className="p-text-center p-p-5" style={{ color: "#adb5bd" }}>
                                    <i
                                        className="pi pi-folder-open p-d-block p-mb-3"
                                        style={{ fontSize: "3.5rem" }}
                                    />
                                    <h3 style={{ fontWeight: "400" }}>
                                        Nenhum dado encontrado para os filtros selecionados.
                                    </h3>
                                </div>
                            ) : (
                                <>
                                    {/* Botão imprimir */}
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            marginBottom: 16,
                                            gap: 10,
                                        }}
                                    >
                                        <span
                                            style={{
                                                alignSelf: "center",
                                                color: "#6c757d",
                                                fontSize: 13,
                                            }}
                                        >
                                            {resultado.length} operador(es) encontrado(s)
                                        </span>
                                        <Button
                                            icon="pi pi-print"
                                            label="Imprimir Relatório"
                                            className="p-button"
                                            style={{ backgroundColor: "#115a77", borderColor: "#115a77" }}
                                            onClick={handlePrint}
                                        />
                                    </div>

                                    {/* Preview visual na tela */}
                                    <PreviewOperador
                                        resultado={resultado}
                                        initialDate={initialDate}
                                        finalDate={finalDate}
                                        filtroTurma={filtroTurma}
                                    />

                                    {/* Área oculta exclusiva para impressão */}
                                    <div style={{ position: "absolute", left: "-9999px", top: 0, width: "210mm" }}>
                                        <RelatorioImpressao
                                            innerRef={printRef}
                                            resultado={resultado}
                                            initialDate={initialDate}
                                            finalDate={finalDate}
                                            filtroTurma={filtroTurma}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* Estado inicial vazio */}
                    {!loading && historicoRaw.length === 0 && (
                        <div className="p-text-center p-p-5" style={{ color: "#adb5bd" }}>
                            <i
                                className="pi pi-search p-d-block p-mb-3"
                                style={{ fontSize: "3.5rem" }}
                            />
                            <h3 style={{ fontWeight: "400" }}>
                                Selecione o período e clique em buscar para carregar os dados.
                            </h3>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
